import { ReactElement, useState } from 'react'

import {
  T,
  getAllLocales,
  getAvailableLocales,
  useI18n,
} from '@tribeplatform/react-components/i18n'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useNetwork,
  useUpdateNetwork,
  useUpdateNetworkAvailableLocals,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { CountryLanguage } from '../../../Settings/CountryLanguage.js'

export const CommunityLanguage = () => {
  const { $t } = useI18n()
  const { Link: RouterLink } = useRouter()
  const { data: network } = useNetwork({
    fields: {
      limits: 'all',
    },
  })
  const { mutateAsync: updateNetwork, isLoading: isUpdatingNetwork } =
    useUpdateNetwork()
  const {
    mutateAsync: updateNetworkAvailableLocals,
    isLoading: isUpdatingLocale,
  } = useUpdateNetworkAvailableLocals()
  const [availableLocales, setAvailableLocales] = useState(
    network?.availableLocales.map(locale => locale.toLowerCase()) ?? ['en-us'],
  )
  const [defaultLocale, setDefaultLocale] = useState(
    network?.locale?.toLowerCase() ?? 'en-us',
  )

  const allAvailableLocales = getAvailableLocales()
  const allLocales = getAllLocales()

  const availableLocaleSelectItems = availableLocales.map(locale => ({
    value: locale,
    text: <CountryLanguage locale={locale} />,
  }))

  const allLocaleMultiselectLabels = allLocales.reduce(
    (labels, locale) => ({
      ...labels,
      [locale]: <CountryLanguage locale={locale} />,
    }),
    {} as Record<string, ReactElement>,
  )

  const localesLimit =
    network?.limits?.usageLimits?.translationLocaleCapacity ?? Infinity
  const isExceedingLimit = availableLocales.length > localesLimit

  const submit = async () => {
    try {
      await updateNetworkAvailableLocals({
        availableLocales,
      })
      await updateNetwork({
        input: {
          locale: defaultLocale,
        },
      })
      toast({
        title: $t({
          defaultMessage: 'Localization settings updated',
          id: 'Admin.LanguageSettings.LocalizationSettingsUpdated',
        }),
        status: 'success',
      })
    } catch (error) {
      toast({
        title: $t({
          defaultMessage: 'Failed to update localization settings',
          id: 'Admin.LanguageSettings.FailedToUpdateLocalizationSettings',
        }),
        status: 'error',
      })
    }
  }

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: '{COMMUNITY_CC} language',
          id: 'Admin.LanguageSettings.CommunityLanguage',
        })}
      />
      <Card.Content>
        <div className="flex flex-col space-y-5">
          <FormControl.Multiselect
            name="availableLocales"
            label={$t({
              defaultMessage: 'Available languages',
              id: 'Admin.LanguageSettings.AvailableLanguages',
            })}
            helperText={$t({
              defaultMessage:
                'Defines the languages available for members and guests to choose from.',
              id: 'Admin.LanguageSettings.AvailableLanguages.HelperText',
            })}
            value={availableLocales}
            onChange={value => {
              if (!value.includes(defaultLocale) && value.length > 0) {
                setDefaultLocale(value[0])
              }
              setAvailableLocales(value)
            }}
            options={allAvailableLocales}
            labels={allLocaleMultiselectLabels}
            invalid={isExceedingLimit}
            error={
              <T
                defaultMessage="The number of available languages exceeds the limit of {limit}. <link>Manage usages</link>."
                id="Admin.LanguageSettings.AvailableLanguages.ExceedingLimit"
                values={{
                  limit: localesLimit,
                  link: chunks => (
                    <Link as={RouterLink} href="/manage/billing/usages">
                      {chunks}
                    </Link>
                  ),
                }}
              />
            }
          />
          <FormControl.Select
            name="locale"
            label={$t({
              defaultMessage: 'Default language',
              id: 'Admin.LanguageSettings.Community',
            })}
            value={defaultLocale}
            onChange={setDefaultLocale}
            helperText={$t({
              defaultMessage:
                'Defines the default language for new members and guests.',
              id: 'Admin.LanguageSettings.Community.HelperText',
            })}
            items={availableLocaleSelectItems}
          />
        </div>
      </Card.Content>
      <Card.Actions>
        <Button
          type="button"
          loading={isUpdatingLocale || isUpdatingNetwork}
          disabled={
            isExceedingLimit || availableLocales.length === 0 || !defaultLocale
          }
          onClick={submit}
        >
          <T defaultMessage="Update" id="Generics.Update" />
        </Button>
      </Card.Actions>
    </Card>
  )
}
